import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  LogOut,
  MapPin,
  Pencil,
  Phone,
  Mail,
} from "lucide-react";

import { CustomHeader } from "../components/AuthHeader";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAuthStore } from "../features/auth/authStore";
import { useToast } from "../components/ToastProvider";

export default function ProfileScreen() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, logout, isLoading } = useAuthStore();

  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setShowLogoutDialog(false);
      showToast("Logged out successfully", "success");
      navigate("/login");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Logout failed";
      showToast(message, "error");
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  const fullName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(" ");

  const initials = [
    user?.first_name?.[0],
    user?.last_name?.[0],
  ]
    .filter(Boolean)
    .join("")
    .toUpperCase();

  return (
    <div className="bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white px-6 pt-5 pb-1 shadow-sm rounded-b-3xl mb-6">
        <CustomHeader
          title="Profile"
          subtitle="Manage your account & preferences"
        />
      </div>

      <div className="px-5">
        {/* Profile Card */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 mb-6">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full bg-[#44B6CE] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl">
                {initials || "?"}
              </span>
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold font-poppins text-gray-900 truncate">
                {fullName || "—"}
              </h2>

              <p className="text-sm text-gray-400 truncate">
                {user?.email || "No email"}
              </p>
            </div>
          </div>

          {/* Edit Profile Button */}
          <button
            onClick={() => navigate("/profile/edit")}
            className="mt-5 w-full h-11 rounded-2xl bg-[#44B6CE] text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition"
          >
            <Pencil size={16} />
            Edit Profile
          </button>
        </div>

        {/* Preferences Section */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
            Preferences
          </p>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Locations */}
            <button
              onClick={() => navigate("/profile/locations")}
              className="w-full flex items-center justify-between px-5 py-4 active:bg-gray-50 transition"
            >
              <div className="flex items-center gap-4 text-left">
                <div className="w-10 h-10 rounded-xl bg-[#44B6CE]/10 flex items-center justify-center">
                  <MapPin size={18} className="text-[#44B6CE]" />
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Locations
                  </p>

                  <p className="text-xs text-gray-400">
                    Manage saved pickup & delivery addresses
                  </p>
                </div>
              </div>

              <ChevronRight size={18} className="text-gray-300" />
            </button>
          </div>
        </div>

        {/* Account Information */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
            Account Information
          </p>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Phone */}
            <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-50">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                <Phone size={18} className="text-gray-500" />
              </div>

              <div>
                <p className="text-xs text-gray-400 mb-0.5">
                  Mobile / WhatsApp
                </p>

                <p className="text-sm font-medium text-gray-800">
                  {user?.phone || "—"}
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-center gap-4 px-5 py-4">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                <Mail size={18} className="text-gray-500" />
              </div>

              <div>
                <p className="text-xs text-gray-400 mb-0.5">
                  Email Address
                </p>

                <p className="text-sm font-medium text-gray-800">
                  {user?.email || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Logout */}
        <button
          disabled={isLoading}
          onClick={handleLogoutClick}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border border-red-100 bg-red-50 text-red-500 font-semibold text-sm transition hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogOut size={18} />

          {isLoading ? "Logging out..." : "Logout"}
        </button>
      </div>

      {/* Logout Confirmation */}
      <ConfirmDialog
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        title="Logout"
        description="Are you sure you want to logout? You'll need to login again to access your account."
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        confirmText="Yes, Logout"
        cancelText="Cancel"
        isLoading={isLoading}
      />
    </div>
  );
}